import React, { useState, useEffect, useCallback } from 'react';
import { 
  Card, 
  Layout, 
  Typography, 
  Space, 
  Button, 
  Modal, 
  Form, 
  Input, 
  Select, 
  message, 
  List, 
  Avatar, 
  Dropdown, 
  theme as antdTheme,
  Empty,
  Spin,
  Divider,
  Statistic,
  Tag,
  Alert,
  Flex,
  Badge,
  Tooltip,
  Row,
  Col
} from 'antd';
import {
  MessageOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  MoreOutlined,
  RobotOutlined,
  UserOutlined,
  SendOutlined,
  KeyOutlined,
  ApiOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { Bubble, Sender, Conversations } from '@ant-design/x';
import { chatApi, apiKeyApi, authApi, modelSelectionApi } from '../../services/api';
import { usePermissionCheck, PermissionGuard } from '../../context/PermissionContext';
import type { ChatSession, ChatMessage, CreateChatSessionRequest, SendMessageRequest, ApiKey, ModelOption } from '../../types';

const { Title, Text } = Typography;
const { Sider, Content } = Layout;
const { TextArea } = Input;

const ChatInterface: React.FC = () => {
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [currentSession, setCurrentSession] = useState<ChatSession | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [messageLoading, setMessageLoading] = useState(false);
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editingSession, setEditingSession] = useState<ChatSession | null>(null);
  const [inputMessage, setInputMessage] = useState('');
  const [senderValue, setSenderValue] = useState(''); // 添加Sender组件的受控状态
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  
  // API密钥相关状态
  const [currentApiKey, setCurrentApiKey] = useState<string>('');
  const [apiKeyStatus, setApiKeyStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [userApiKeys, setUserApiKeys] = useState<ApiKey[]>([]);
  const [balance, setBalance] = useState<number>(0);
  
  // 模型相关状态
  const [availableModels, setAvailableModels] = useState<ModelOption[]>([]);
  const [modelsLoading, setModelsLoading] = useState(true);
  
  const { token } = antdTheme.useToken();
  const {
    canViewChat,
    canCreateChat,
    canEditChat,
    canDeleteChat,
    canSendMessage,
    canViewChatHistory,
  } = usePermissionCheck();

  // 移除X Chat hook，直接使用组件

  // 获取支持的模型列表
  const fetchSupportedModels = useCallback(async () => {
    try {
      setModelsLoading(true);
      const response = await modelSelectionApi.getSupportedModels();
      const models = response.data.models || [];
      const enabledModels = models.filter((model: ModelOption) => model.is_enabled);
      setAvailableModels(enabledModels);
      console.log('✅ 支持的模型列表加载完成:', enabledModels.length);
    } catch (error: any) {
      console.error('❌ 加载支持的模型列表失败:', error);
      message.error('加载模型列表失败');
      setAvailableModels([
        { 
          name: 'deepseek-chat', 
          display_name: 'DeepSeek Chat', 
          provider: 'deepseek',
          type: 'text',
          max_tokens: 8192,
          input_price: 0.001,
          output_price: 0.002,
          is_enabled: true,
          description: 'DeepSeek Chat model'
        },
        { 
          name: 'deepseek-reasoner', 
          display_name: 'DeepSeek Reasoner', 
          provider: 'deepseek',
          type: 'text',
          max_tokens: 8192,
          input_price: 0.001,
          output_price: 0.002,
          is_enabled: true,
          description: 'DeepSeek Reasoner model'
        },
      ]);
    } finally {
      setModelsLoading(false);
    }
  }, []);

  // 获取当前用户API密钥
  const fetchCurrentUserApiKey = useCallback(async () => {
    try {
      setApiKeyStatus('loading');
      
      const currentUserResponse = await authApi.getCurrentUser();
      const currentUser = currentUserResponse.data;
      if (!currentUser?.id) {
        throw new Error('无法获取当前用户信息');
      }

      // 设置余额
      setBalance(currentUser.balance || 0);

      try {
        const apiResponse = await apiKeyApi.getOrCreateMyApiKey();
        const selectedApi = apiResponse.data;
        
        setCurrentApiKey(selectedApi.api_key);
        setApiKeyStatus('ready');
        
        const apiKeysResponse = await apiKeyApi.getUserApiKeys(currentUser.id);
        const apiKeys = Array.isArray(apiKeysResponse.data?.apis) ? apiKeysResponse.data.apis : [];
        setUserApiKeys(apiKeys);
        
        return;
      } catch (userApiError) {
        console.log('用户智能获取接口调用失败，回退到手动选择模式:', userApiError);
      }

      const apiKeysResponse = await apiKeyApi.getUserApiKeys(currentUser.id);
      const apiKeys = Array.isArray(apiKeysResponse.data?.apis) ? apiKeysResponse.data.apis : [];
      setUserApiKeys(apiKeys);

      let selectedKey = apiKeys.find((key: ApiKey) => key.is_active);
      
      if (!selectedKey && apiKeys.length > 0) {
        selectedKey = apiKeys[0];
      }
      
      if (selectedKey) {
        setCurrentApiKey(selectedKey.api_key);
        setApiKeyStatus('ready');
      } else {
        if (apiKeys.length === 0) {
          try {
            const newApiKeyResponse = await apiKeyApi.createApiKey(currentUser.id, {
              name: 'AI聊天专用密钥',
              description: '用于AI聊天功能的自动生成密钥',
            });
            
            setCurrentApiKey(newApiKeyResponse.data.api_key);
            setApiKeyStatus('ready');
            
            const updatedKeysResponse = await apiKeyApi.getUserApiKeys(currentUser.id);
            const updatedKeys = Array.isArray(updatedKeysResponse.data?.apis) ? updatedKeysResponse.data.apis : [];
            setUserApiKeys(updatedKeys);
          } catch (createError) {
            console.error('创建API密钥失败:', createError);
            setApiKeyStatus('error');
            message.error('无法创建API密钥，请联系管理员或到API密钥管理页面手动创建');
          }
        } else {
          setApiKeyStatus('error');
          message.warning('您的API密钥可能已被禁用，请到API密钥管理页面检查');
        }
      }
      
    } catch (error) {
      console.error('获取API密钥失败:', error);
      setApiKeyStatus('error');
      message.error('获取API密钥失败，请检查网络连接或联系管理员');
    }
  }, []);

  // 获取会话列表
  const fetchSessions = useCallback(async () => {
    try {
      setSessionLoading(true);
      console.log('📋 开始获取会话列表...');
      
      const response = await chatApi.getSessions();
      console.log('✅ 会话列表获取成功，响应数据:', response.data);
      
      // 修复数据结构访问：后端返回 { data: { sessions: [...], total: n } }
      const sessions = response.data.data.sessions || [];
      console.log('📋 解析到的会话数量:', sessions.length);
      console.log('📋 会话列表:', sessions);
      
      setSessions(sessions);
    } catch (error: any) {
      console.error('❌ 获取会话列表失败，错误详情:', error);
      console.error('❌ 错误响应:', error.response?.data);
      console.error('❌ 错误状态码:', error.response?.status);
      
      let errorMessage = '获取会话列表失败';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      message.error(`获取会话列表失败: ${errorMessage}`);
    } finally {
      setSessionLoading(false);
    }
  }, []);

  // 获取消息列表
  const fetchMessages = useCallback(async (sessionId: number) => {
    try {
      console.log('📋 开始获取消息列表，会话ID:', sessionId);
      const response = await chatApi.getMessages(sessionId);
      console.log('✅ 消息列表获取成功，响应数据:', response.data);
      
      // 修复数据结构访问：后端返回 { data: { messages: [...], total: n } }
      const messages = response.data.data.messages || [];
      console.log('📋 解析到的消息数量:', messages.length);
      console.log('📋 消息列表:', messages);
      
      setMessages(messages);
    } catch (error: any) {
      console.error('❌ 获取消息列表失败，错误详情:', error);
      console.error('❌ 错误响应:', error.response?.data);
      console.error('❌ 错误状态码:', error.response?.status);
      
      let errorMessage = '获取消息列表失败';
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      message.error(`获取消息列表失败: ${errorMessage}`);
    }
  }, []);

  // 选择会话
  const handleSessionSelect = useCallback(async (session: ChatSession) => {
    setCurrentSession(session);
    setSenderValue(''); // 切换会话时清空输入框
    await fetchMessages(session.id);
  }, [fetchMessages]);

  // 创建会话
  const handleCreateSession = async (values: CreateChatSessionRequest) => {
    console.log('🚀 开始创建会话，表单数据:', values);
    
    // 检查权限
    if (!canCreateChat()) {
      console.error('❌ 没有创建会话的权限');
      message.error('您没有权限创建会话');
      return;
    }

    // 检查API密钥状态
    if (apiKeyStatus !== 'ready') {
      console.error('❌ API密钥未就绪，状态:', apiKeyStatus);
      message.error('API密钥未就绪，无法创建会话');
      return;
    }

    try {
      setLoading(true);
      console.log('📡 调用创建会话API...');
      
      const response = await chatApi.createSession(values);
      console.log('✅ 会话创建成功，响应数据:', response.data);
      
      message.success('会话创建成功');
      setIsCreateModalVisible(false);
      form.resetFields();
      
      console.log('🔄 刷新会话列表...');
      await fetchSessions();
      console.log('✅ 会话列表刷新完成');
      
      // 自动选择新创建的会话
      if (response.data && response.data.id) {
        console.log('🎯 自动选择新创建的会话:', response.data.id);
        setCurrentSession(response.data);
        await fetchMessages(response.data.id);
      }
      
    } catch (error: any) {
      console.error('❌ 创建会话失败，错误详情:', error);
      console.error('❌ 错误响应:', error.response?.data);
      console.error('❌ 错误状态码:', error.response?.status);
      
      let errorMessage = '创建会话失败';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      message.error(`创建会话失败: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  // 编辑会话
  const handleEditSession = async (values: any) => {
    if (!editingSession) return;
    
    try {
      setLoading(true);
      await chatApi.updateSession(editingSession.id, values);
      message.success('会话更新成功');
      setIsEditModalVisible(false);
      setEditingSession(null);
      editForm.resetFields();
      await fetchSessions();
    } catch (error) {
      console.error('编辑会话失败:', error);
      message.error('编辑会话失败');
    } finally {
      setLoading(false);
    }
  };

  // 删除会话
  const handleDeleteSession = async (sessionId: number) => {
    try {
      await chatApi.deleteSession(sessionId);
      message.success('会话删除成功');
      if (currentSession?.id === sessionId) {
        setCurrentSession(null);
        setMessages([]);
      }
      await fetchSessions();
    } catch (error) {
      console.error('删除会话失败:', error);
      message.error('删除会话失败');
    }
  };

  // 发送消息
  const handleSendMessage = async (text: string) => {
    if (!currentSession) {
      message.error('请先选择一个会话');
      return;
    }

    if (!text.trim()) {
      message.error('消息内容不能为空');
      return;
    }

    if (apiKeyStatus !== 'ready' || !currentApiKey) {
      message.error('API密钥未就绪，无法发送消息');
      return;
    }

    try {
      setMessageLoading(true);
      
      // 立即清空输入框，提升用户体验
      setSenderValue('');

      // 添加用户消息到本地状态
      const userMessage: ChatMessage = {
        id: Date.now(), // 临时ID
        session_id: currentSession.id,
        role: 'user' as const,
        content: text.trim(),
        token_count: Math.ceil(text.trim().length / 4), // 估算token数量
        cost: 0,
        created_at: new Date().toISOString(),
      };
      
      setMessages(prev => [...prev, userMessage]);

      // 添加一个空的助手消息，用于流式显示
      const assistantMessageId = Date.now() + 1;
      const assistantMessage: ChatMessage = {
        id: assistantMessageId,
        session_id: currentSession.id,
        role: 'assistant' as const,
        content: '',
        token_count: 0,
        cost: 0,
        created_at: new Date().toISOString(),
      };
      
      setMessages(prev => [...prev, assistantMessage]);

      // 获取会话历史构建完整提示词
      const history = messages.slice(-10); // 取最近10条消息作为上下文
      let prompt = '';
      
      // 添加系统提示词
      if (currentSession.system_prompt) {
        prompt += `系统提示: ${currentSession.system_prompt}\n\n`;
      }
      
      // 添加历史消息
      history.forEach(msg => {
        if (msg.role === 'user') {
          prompt += `用户: ${msg.content}\n\n`;
        } else if (msg.role === 'assistant') {
          prompt += `助手: ${msg.content}\n\n`;
        }
      });
      
      // 添加当前用户消息
      prompt += `用户: ${text.trim()}\n\n助手: `;

      // 使用/api/v1/generate接口进行流式生成
      const baseUrl = process.env.REACT_APP_API_BASE_URL || 'https://apilink.haotunet.com';
      const url = `${baseUrl}/api/v1/generate`;
      
      // 构建请求体
      const requestBody = {
        prompt: prompt,
        model_name: currentSession.model_name || 'deepseek-chat',
        stream: true,
      };
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': currentApiKey,
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error(`API请求失败: ${response.status} ${response.statusText}`);
      }

      if (!response.body) {
        throw new Error('响应体为空');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let assistantContent = '';
      let currentEvent = '';
      let buffer = '';
      let tokenCount = 0;
      let cost = 0;

      try {
        while (true) {
          const { done, value } = await reader.read();
          
          if (done) {
            break;
          }

          const chunk = decoder.decode(value, { stream: true });
          buffer += chunk;
          
          const lines = buffer.split('\n');
          buffer = lines[lines.length - 1];
          
          for (let i = 0; i < lines.length - 1; i++) {
            const line = lines[i].trim();
            
            if (!line) {
              continue;
            }
            
            if (line.startsWith('event:')) {
              currentEvent = line.slice(6).trim();
            } else if (line.startsWith('data:')) {
              const data = line.slice(5).trim();
              
              if (data === '[DONE]' || currentEvent === 'done') {
                break;
              }
              
              if (currentEvent === 'message') {
                if (data && data !== '[DONE]') {
                  if (data.startsWith('{') && data.endsWith('}')) {
                    try {
                      const parsed = JSON.parse(data);
                      if (parsed.error) {
                        throw new Error(parsed.error);
                      }
                      // 如果是最终响应，可能包含token和费用信息
                      if (parsed.token_count !== undefined) {
                        tokenCount = parsed.token_count;
                      }
                      if (parsed.cost !== undefined) {
                        cost = parsed.cost;
                      }
                    } catch (parseError) {
                      // JSON解析失败，当作文本内容处理
                      assistantContent += data;
                      
                      // 实时更新助手消息
                      setMessages(prev => 
                        prev.map(msg => 
                          msg.id === assistantMessageId 
                            ? { ...msg, content: assistantContent }
                            : msg
                        )
                      );
                    }
                  } else {
                    // 直接当作文本内容处理
                    assistantContent += data;
                    
                    // 实时更新助手消息
                    setMessages(prev => 
                      prev.map(msg => 
                        msg.id === assistantMessageId 
                          ? { ...msg, content: assistantContent }
                          : msg
                      )
                    );
                  }
                }
              } else if (currentEvent === 'error') {
                try {
                  const errorData = JSON.parse(data);
                  throw new Error(errorData.error || '服务器返回错误');
                } catch (parseError) {
                  throw new Error(data || '未知错误');
                }
              }
            }
          }
        }
      } finally {
        reader.releaseLock();
      }

      // 流式传输完成后，调用保存消息接口
      if (assistantContent.trim()) {
        try {
          const saveUrl = `${baseUrl}/api/admin/v1/chat/sessions/${currentSession.id}/save-message`;
          const saveRequestBody = {
            user_message: {
              content: text.trim(),
              token_count: Math.ceil(text.trim().length / 4),
            },
            assistant_message: {
              content: assistantContent.trim(),
              model_name: currentSession.model_name || 'deepseek-chat',
              token_count: tokenCount,
              cost: cost,
            },
          };
          
          const saveResponse = await fetch(saveUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('admin_token')}`,
            },
            body: JSON.stringify(saveRequestBody),
          });

          if (!saveResponse.ok) {
            console.error('保存消息失败:', saveResponse.status, saveResponse.statusText);
            // 保存失败不影响用户体验，只记录错误
          }

          // 重新获取消息列表以获取真实的ID和元数据
          await fetchMessages(currentSession.id);
        } catch (saveError) {
          console.error('保存消息失败:', saveError);
          // 保存失败不影响用户体验，只记录错误
        }
      }

    } catch (error) {
      console.error('发送消息失败:', error);
      
      // 恢复输入框内容
      setSenderValue(text);
      
      // 移除临时消息
      setMessages(prev => prev.filter(msg => 
        msg.id !== Date.now() && msg.id !== Date.now() + 1
      ));
      
      const errorMessage = error instanceof Error ? error.message : '发送消息失败';
      message.error(errorMessage);
    } finally {
      setMessageLoading(false);
    }
  };

  // 检查创建会话的所有条件
  const checkCreateSessionConditions = useCallback(() => {
    const conditions = {
      hasPermission: canCreateChat(),
      apiKeyReady: apiKeyStatus === 'ready',
      hasApiKey: !!currentApiKey,
      hasModels: availableModels.length > 0,
      modelsLoaded: !modelsLoading,
    };
    
    console.log('📋 创建会话条件检查:', conditions);
    
    const allReady = Object.values(conditions).every(Boolean);
    console.log('✅ 所有条件满足:', allReady);
    
    if (!allReady) {
      const missingConditions = Object.entries(conditions)
        .filter(([_, value]) => !value)
        .map(([key, _]) => key);
      console.warn('❌ 缺少的条件:', missingConditions);
    }
    
    return { conditions, allReady };
  }, [canCreateChat, apiKeyStatus, currentApiKey, availableModels.length, modelsLoading]);

  // 组件初始化
  useEffect(() => {
    console.log('🚀 聊天组件初始化开始...');
    console.log('🔑 权限检查结果:', {
      canViewChat: canViewChat(),
      canCreateChat: canCreateChat(),
      canEditChat: canEditChat(),
      canDeleteChat: canDeleteChat(),
      canSendMessage: canSendMessage(),
      canViewChatHistory: canViewChatHistory(),
    });
    
    fetchCurrentUserApiKey();
    fetchSupportedModels();
    fetchSessions();
  }, [fetchCurrentUserApiKey, fetchSupportedModels, fetchSessions]);

  // 监听状态变化，输出创建会话条件
  useEffect(() => {
    const timer = setTimeout(() => {
      checkCreateSessionConditions();
    }, 1000); // 延迟1秒检查，确保所有状态都已更新
    
    return () => clearTimeout(timer);
  }, [checkCreateSessionConditions]);
  


  // 会话操作菜单
  const getSessionMenuItems = (session: ChatSession) => {
    const items = [];
    
    if (canEditChat()) {
      items.push({
        key: 'edit',
        icon: <EditOutlined />,
        label: '编辑',
        onClick: () => {
          setEditingSession(session);
          editForm.setFieldsValue({
            name: session.name,
            model_name: session.model_name,
            system_prompt: session.system_prompt,
          });
          setIsEditModalVisible(true);
        },
      });
    }

    if (canDeleteChat()) {
      items.push({
        key: 'delete',
        icon: <DeleteOutlined />,
        label: '删除',
        danger: true,
        onClick: () => {
          Modal.confirm({
            title: '确认删除',
            content: `确定要删除会话"${session.name}"吗？此操作不可恢复。`,
            okText: '确定',
            cancelText: '取消',
            okType: 'danger',
            onOk: () => handleDeleteSession(session.id),
          });
        },
      });
    }

    return items;
  };

  // 格式化消息时间
  const formatTime = (timeStr: string) => {
    const time = new Date(timeStr);
    const now = new Date();
    const diffInHours = (now.getTime() - time.getTime()) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      return time.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' });
    } else {
      return time.toLocaleDateString('zh-CN', { month: 'short', day: 'numeric' });
    }
  };

  // 转换消息为X Chat格式
  const xChatMessages = messages.map(msg => ({
    id: msg.id.toString(),
    role: msg.role as 'user' | 'assistant',
    content: msg.content,
    timestamp: msg.created_at,
  }));

  // 转换会话为Conversations格式
  const conversationsData = sessions.map(session => ({
    key: session.id.toString(),
    label: session.name,
    timestamp: new Date(session.updated_at).getTime(),
    active: currentSession?.id === session.id,
  }));

  return (
    <PermissionGuard permission="chat.view" fallback={<div>无权限访问聊天功能</div>}>
      <Layout style={{ height: 'calc(100vh - 112px)', background: token.colorBgContainer }}>
        {/* 左侧会话列表 */}
        <Sider 
          width={340} 
          style={{ 
            background: token.colorBgContainer,
            borderRight: `1px solid ${token.colorBorder}`,
            boxShadow: '2px 0 8px rgba(0,0,0,0.06)',
          }}
        >
          <div style={{ padding: '20px 16px' }}>
            {/* API密钥状态和余额信息 */}
            <Card 
              size="small" 
              style={{ 
                marginBottom: '20px',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              }}
              styles={{ body: { padding: '16px' } }}
            >
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <Row align="middle" justify="space-between">
                  <Col>
                    <Space>
                      <KeyOutlined style={{ color: token.colorPrimary }} />
                      <Text strong>API密钥状态</Text>
                    </Space>
                  </Col>
                  <Col>
                    {apiKeyStatus === 'loading' && <Spin size="small" />}
                    {apiKeyStatus === 'ready' && <Badge status="success" text="就绪" />}
                    {apiKeyStatus === 'error' && <Badge status="error" text="异常" />}
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={8}>
                    <Statistic
                      title="账户余额"
                      value={balance}
                      precision={2}
                      prefix={<DollarOutlined />}
                      valueStyle={{ fontSize: '14px' }}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="密钥数量"
                      value={userApiKeys.length}
                      prefix={<ApiOutlined />}
                      valueStyle={{ fontSize: '14px' }}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="会话数量"
                      value={sessions.length}
                      prefix={<MessageOutlined />}
                      valueStyle={{ fontSize: '14px' }}
                    />
                  </Col>
                </Row>

                {currentApiKey && (
                  <Row align="middle" justify="space-between">
                    <Col>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        当前密钥: {currentApiKey.substring(0, 10)}...
                      </Text>
                    </Col>
                    <Col>
                      <Tag color="blue" icon={<ThunderboltOutlined />}>
                        活跃
                      </Tag>
                    </Col>
                  </Row>
                )}
              </Space>
            </Card>

            {/* 会话列表 */}
            <Card 
              title={
                <Row align="middle" justify="space-between">
                  <Col>
                    <Space>
                      <MessageOutlined />
                      <Text strong>聊天会话</Text>
                    </Space>
                  </Col>
                  <Col>
                    <PermissionGuard permission="chat.create_session">
                      <Tooltip title={
                        apiKeyStatus !== 'ready' ? 'API密钥未就绪' :
                        availableModels.length === 0 ? '暂无可用模型' :
                        '创建新会话'
                      }>
                        <Button 
                          type="primary" 
                          icon={<PlusOutlined />} 
                          onClick={() => {
                            const { conditions, allReady } = checkCreateSessionConditions();
                            if (!allReady) {
                              const issues = [];
                              if (!conditions.hasPermission) issues.push('没有创建会话权限');
                              if (!conditions.apiKeyReady) issues.push('API密钥未就绪');
                              if (!conditions.hasModels) issues.push('暂无可用模型');
                              if (!conditions.modelsLoaded) issues.push('模型列表加载中');
                              
                              message.warning(`无法创建会话：${issues.join('、')}`);
                              return;
                            }
                            setIsCreateModalVisible(true);
                          }}
                          size="small"
                          disabled={apiKeyStatus !== 'ready' || availableModels.length === 0}
                          shape="round"
                        >
                          新建
                        </Button>
                      </Tooltip>
                    </PermissionGuard>
                  </Col>
                </Row>
              }
              styles={{ body: { padding: '16px 0' } }}
              style={{ 
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              }}
            >
              {sessionLoading ? (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <Spin size="large" />
                  <div style={{ marginTop: '10px' }}>
                    <Text type="secondary">加载会话列表...</Text>
                  </div>
                </div>
              ) : sessions.length === 0 ? (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="暂无会话"
                  style={{ margin: '20px 0' }}
                >
                  <PermissionGuard permission="chat.create_session">
                    <Button 
                      type="primary" 
                      icon={<PlusOutlined />} 
                      onClick={() => setIsCreateModalVisible(true)}
                      disabled={apiKeyStatus !== 'ready'}
                    >
                      创建第一个会话
                    </Button>
                  </PermissionGuard>
                </Empty>
              ) : (
                // 使用@ant-design/x的Conversations组件
                <Conversations
                  items={conversationsData}
                  onActiveChange={(key) => {
                    const session = sessions.find(s => s.id.toString() === key);
                    if (session) {
                      handleSessionSelect(session);
                    }
                  }}
                  activeKey={currentSession?.id.toString()}
                  style={{ height: '300px' }}
                />
              )}
            </Card>
          </div>
        </Sider>

        {/* 右侧聊天区域 */}
        <Content style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {!currentSession ? (
            <div style={{ 
              flex: 1, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              background: token.colorBgContainer,
            }}>
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="请选择一个会话开始聊天"
              />
            </div>
          ) : (
            <>
              {/* 会话头部 */}
              <div style={{ 
                padding: '16px 24px', 
                borderBottom: `1px solid ${token.colorBorder}`,
                background: token.colorBgContainer,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}>
                <Space>
                  <Avatar icon={<RobotOutlined />} style={{ backgroundColor: token.colorPrimary }} />
                  <div>
                    <Text strong style={{ fontSize: '16px' }}>{currentSession.name}</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      模型: {currentSession.model_name}
                    </Text>
                  </div>
                </Space>
                <Space>
                  <Badge count={messages.length} showZero style={{ backgroundColor: token.colorPrimary }} />
                  <Dropdown
                    menu={{
                      items: getSessionMenuItems(currentSession),
                    }}
                    trigger={['click']}
                    placement="bottomRight"
                  >
                    <Button 
                      type="text" 
                      icon={<MoreOutlined />} 
                      size="small"
                    />
                  </Dropdown>
                </Space>
              </div>

              {/* 聊天消息区域 */}
              <div style={{ 
                flex: 1, 
                padding: '16px 24px',
                overflowY: 'auto',
                background: token.colorBgLayout,
              }}>
                {messages.length === 0 ? (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="开始对话吧"
                    style={{ marginTop: '50px' }}
                  />
                ) : (
                  <div style={{ maxWidth: '800px', margin: '0 auto' }}>
                    {xChatMessages.map((msg, index) => (
                      <div key={msg.id} style={{ marginBottom: '16px' }}>
                        <Bubble
                          content={msg.content}
                          avatar={msg.role === 'user' ? { icon: <UserOutlined /> } : { icon: <RobotOutlined /> }}
                          variant={msg.role === 'user' ? 'filled' : 'outlined'}
                          placement={msg.role === 'user' ? 'end' : 'start'}
                          typing={index === messages.length - 1 && messageLoading && msg.role === 'assistant'}
                          style={{ marginBottom: '8px' }}
                        />
                        <div style={{ 
                          textAlign: msg.role === 'user' ? 'right' : 'left',
                          marginBottom: '8px'
                        }}>
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            {formatTime(msg.timestamp)}
                          </Text>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* 输入区域 */}
              <div style={{ 
                padding: '16px 24px', 
                borderTop: `1px solid ${token.colorBorder}`,
                background: token.colorBgContainer,
              }}>
                {canSendMessage() ? (
                  <div style={{ maxWidth: '800px', margin: '0 auto' }}>
                    <Sender
                      loading={messageLoading}
                      onSubmit={handleSendMessage}
                      placeholder="输入消息..."
                      disabled={!currentSession || apiKeyStatus !== 'ready'}
                      style={{ borderRadius: '12px' }}
                      value={senderValue}
                      onChange={setSenderValue}
                    />
                  </div>
                ) : (
                  <Alert
                    message="您没有发送消息的权限"
                    type="warning"
                    showIcon
                    style={{ borderRadius: '8px' }}
                  />
                )}
              </div>
            </>
          )}
        </Content>
      </Layout>

      {/* 创建会话弹窗 */}
      <Modal
        title="创建新会话"
        open={isCreateModalVisible}
        onCancel={() => setIsCreateModalVisible(false)}
        footer={null}
        width={600}
        destroyOnHidden
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreateSession}
          preserve={false}
        >
          <Form.Item
            label="会话名称"
            name="name"
            rules={[
              { required: true, message: '请输入会话名称' },
              { min: 1, max: 50, message: '会话名称长度应在1-50个字符之间' }
            ]}
          >
            <Input 
              placeholder="请输入会话名称" 
              maxLength={50}
              showCount
            />
          </Form.Item>

          <Form.Item
            label="AI模型"
            name="model_name"
            rules={[{ required: true, message: '请选择AI模型' }]}
            initialValue="deepseek-chat"
          >
            <Select 
              placeholder="请选择AI模型"
              loading={modelsLoading}
              options={availableModels.map(model => ({
                value: model.name,
                label: `${model.display_name} (${model.provider})`,
                disabled: !model.is_enabled
              }))}
              notFoundContent={modelsLoading ? "加载中..." : "暂无可用模型"}
            />
          </Form.Item>

          <Form.Item
            label="系统提示词"
            name="system_prompt"
            tooltip="系统提示词用于设定AI的角色和行为模式，留空将使用默认设置"
          >
            <Input.TextArea 
              rows={4} 
              placeholder="你是一个有用的AI助手，请根据用户的问题提供准确、有帮助的回答..."
              maxLength={500}
              showCount
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Space>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={loading}
                disabled={!canCreateChat() || apiKeyStatus !== 'ready'}
              >
                创建会话
              </Button>
              <Button onClick={() => {
                setIsCreateModalVisible(false);
                form.resetFields();
              }}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 编辑会话弹窗 */}
      <Modal
        title="编辑会话"
        open={isEditModalVisible}
        onCancel={() => setIsEditModalVisible(false)}
        footer={null}
        width={600}
        destroyOnHidden
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleEditSession}
          preserve={false}
        >
          <Form.Item
            label="会话名称"
            name="name"
            rules={[
              { required: true, message: '请输入会话名称' },
              { min: 1, max: 50, message: '会话名称长度应在1-50个字符之间' }
            ]}
          >
            <Input 
              placeholder="请输入会话名称" 
              maxLength={50}
              showCount
            />
          </Form.Item>

          <Form.Item
            label="AI模型"
            name="model_name"
            rules={[{ required: true, message: '请选择AI模型' }]}
          >
            <Select 
              placeholder="请选择AI模型"
              loading={modelsLoading}
              options={availableModels.map(model => ({
                value: model.name,
                label: `${model.display_name} (${model.provider})`,
                disabled: !model.is_enabled
              }))}
              notFoundContent={modelsLoading ? "加载中..." : "暂无可用模型"}
            />
          </Form.Item>

          <Form.Item
            label="系统提示词"
            name="system_prompt"
            tooltip="系统提示词用于设定AI的角色和行为模式，留空将使用默认设置"
          >
            <Input.TextArea 
              rows={4} 
              placeholder="你是一个有用的AI助手，请根据用户的问题提供准确、有帮助的回答..."
              maxLength={500}
              showCount
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Space>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={loading}
                disabled={!canEditChat()}
              >
                更新会话
              </Button>
              <Button onClick={() => {
                setIsEditModalVisible(false);
                setEditingSession(null);
                editForm.resetFields();
              }}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </PermissionGuard>
  );
};

export default ChatInterface; 